import styled, { css } from 'styled-components';
import { ifProp } from 'styled-tools';
import { Link } from 'react-router-dom';
import { media, layout } from '@prisjakt/ui-base';
import { colors, remCalc } from '@prisjakt/ui-base/styles/variables';
import RatingComponent from '@prisjakt/ui-rating';
import Button from '@prisjakt/ui-button';
import ProductCheckbox from '@prisjakt/ui-product-checkbox';

const darkOverlay = css`
  position: absolute;
  content: '';
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.02);
`;

export const Card = styled.div.attrs({ 'data-test': 'ProductCard' })`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 0 50%;
  align-self: stretch;
  width: 100%;
  min-height: 100%;
  min-width: ${layout.productCardMinWidthMobile};
  max-width: ${layout.productCardMaxWidth};
  text-align: ${ifProp('textCenter', 'center', 'left')};
  border-bottom: ${p =>
    p.hasDivider ? `1px solid ${p.theme.lines.primary}` : 'none'};

  ${media.mediumUp`
    flex: 1 0 ${layout.productCardMinWidth};
  `};
`;

export const ProductLink = styled(Link)`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  position: relative;
  outline: none;
  padding: ${layout.productCardPadding};

  @media (pointer: fine) {
    &:hover::after,
    &:focus::after {
      ${darkOverlay}
    }
  }
`;

export const CampaignBadge = styled.span`
  max-height: 55px;
  max-width: 55px;
  position: absolute;
  top: 0;
  left: 0;
  padding: 1rem;
`;

export const ProductImage = styled.div`
  align-items: center;
  box-sizing: content-box;
  order: 0;
  text-align: center;
  height: ${layout.productCardImageSizeSmall};
  width: ${layout.productCardImageSizeSmall};
  margin: 10px auto 20px auto;
  background: ${colors.lightGray1};
  border-radius: ${layout.borderRadius};

  ${media.mediumUp`
    height: ${layout.productCardImageSizeLarge};
    width: ${layout.productCardImageSizeLarge};
  `};
`;

export const BrandLogo = styled.span`
  max-height: 55px;
  max-width: 55px;
  position: absolute;
  top: 0;
  right: 0;
  padding: 1rem;
`;

export const ProductDetails = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: ${ifProp('stretch', '1 0 auto', '0 0 auto')};
`;

export const Rating = styled(RatingComponent)`
  position: absolute;
  bottom: 100%;
  padding: 2px;
  align-self: center;
  background: ${colors.white};
`;

export const ProductName = styled.div.attrs({ 'data-test': 'ProductName' })`
  margin-top: 5px;
`;

export const Subtitle = styled.div`
  color: ${p => p.theme.text.tertiary};
  font-size: ${remCalc(13)};

  ${media.mediumUp`
    font-size: ${remCalc(12)};
  `};
`;

export const ExtraFooter = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  font-size: 13px;
  color: ${p => p.theme.text.tertiary};

  ${media.mediumUp`
    font-size: 12px;
  `};
`;

export const Footer = styled.div`
  display: block;
  position: relative;
  outline: none;

  @media (pointer: fine) {
    ${/* sc-selector */ ProductLink}:hover + &::after,
    ${/* sc-selector */ ProductLink}:focus + &::after {
      ${darkOverlay}
    }
  }
`;

export const CategoryArea = styled.div`
  display: block;
  padding: 0 1rem 1rem;
  position: relative;
  outline: none;

  @media (pointer: fine) {
    ${/* sc-selector */ ProductLink}:hover + &::after,
    ${/* sc-selector */ ProductLink}:focus + &::after {
      ${darkOverlay}
    }
  }
`;

export const CategoryLink = styled(Button)`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  box-sizing: border-box;

  && {
    text-transform: none;
  }

  &:hover,
  &:focus {
    color: ${p => p.theme.text.callToActionHover};
  }
`;

export const DiffPercentage = styled.div`
  background: ${colors.green1};
  position: absolute;
  top: ${layout.productCardPadding};
  left: ${layout.productCardPadding};
  padding: 0.1rem 0.4rem;
  font-weight: bold;
  color: ${colors.white};
  border-radius: ${layout.borderRadius};
`;

export const StoreLogo = styled.div`
  margin-left: 10px;
  max-width: 40%;
`;

export const PriceInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
`;

export const SelectBox = styled(ProductCheckbox).attrs(() => ({
  vertical: false,
}))`
  position: absolute;
  top: 10px;
  left: 10px;
  cursor: pointer;
`;

export const SelectBoxWrapper = styled.label`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  &:hover label {
    box-shadow: 0 0 0 2px ${colors.yellow3};
  }
`;
